import { useCallback, useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";
import { api } from "@/lib/api";

const fmtR = (v) => `${v > 0 ? "+" : ""}${v}R`;

function StatCells({ s }) {
  const col = s.total_r > 0 ? "text-[#22C55E]" : s.total_r < 0 ? "text-[#EF4444]" : "text-zinc-400";
  return (
    <>
      <td className="px-3 py-2.5 text-center font-mono text-zinc-300">{s.trades}</td>
      <td className="px-3 py-2.5 text-center font-mono text-zinc-300">{s.win_pct}%</td>
      <td className={`px-3 py-2.5 text-center font-mono font-bold ${col}`}>{fmtR(s.total_r)}</td>
      <td className={`px-3 py-2.5 text-center font-mono ${col}`}>{fmtR(s.avg_r)}</td>
    </>
  );
}

export default function StrategyBacktest({ isAdmin }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (refresh = false) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/backtest/strategy-report${refresh ? "?refresh=1" : ""}`, { timeout: 120000 });
      if (data?.ok) setReport(data);
    } catch { /* transient */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading && !report) {
    return <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 py-16 text-center" data-testid="strategy-backtest-loading">◢ Running strategy simulation…</div>;
  }
  if (!report) {
    return <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 py-16 text-center">Backtest unavailable — try again shortly.</div>;
  }

  const rows = [...report.indices, { symbol: "ALL INDICES", s1: report.combined.s1, s2: report.combined.s2, combined: true }];

  return (
    <div className="space-y-5" data-testid="strategy-backtest">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-bold uppercase tracking-widest">Strategy Backtest — Indian Indices</h2>
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mt-1">
            {report.start} → {report.end} · simulated on live 5-min index data · R = risk unit per trade
          </div>
        </div>
        {isAdmin && (
          <button onClick={() => load(true)} disabled={loading}
                  className="flex items-center gap-2 border border-zinc-800 hover:border-[#007AFF] px-3 py-1.5 text-[10px] font-mono uppercase tracking-widest text-zinc-400 hover:text-white disabled:opacity-50"
                  data-testid="strategy-backtest-refresh">
            <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} /> Recompute
          </button>
        )}
      </div>

      <div className="border border-zinc-800 bg-[#0a0a0a] overflow-x-auto" data-testid="strategy-backtest-table">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-zinc-800">
              <th className="px-3 py-2" />
              <th colSpan={4} className="px-3 py-2 text-center text-[10px] font-mono uppercase tracking-widest text-[#007AFF] border-l border-zinc-800">Strategy 1</th>
              <th colSpan={4} className="px-3 py-2 text-center text-[10px] font-mono uppercase tracking-widest text-[#A855F7] border-l border-zinc-800">Strategy 2</th>
            </tr>
            <tr className="border-b border-zinc-800 text-[9px] font-mono uppercase tracking-widest text-zinc-500">
              <th className="px-3 py-2 text-left">Index</th>
              {["Trades", "Win %", "Net", "Avg"].map((h) => <th key={`s1${h}`} className={`px-3 py-2 text-center ${h === "Trades" ? "border-l border-zinc-800" : ""}`}>{h}</th>)}
              {["Trades", "Win %", "Net", "Avg"].map((h) => <th key={`s2${h}`} className={`px-3 py-2 text-center ${h === "Trades" ? "border-l border-zinc-800" : ""}`}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.symbol} className={`border-b border-zinc-900/60 ${r.combined ? "bg-zinc-900/40 font-bold" : "hover:bg-zinc-900/30"}`} data-testid={`sbt-row-${r.symbol.replace(/\s+/g, "-")}`}>
                <td className="px-3 py-2.5 font-mono uppercase tracking-widest text-white whitespace-nowrap">{r.symbol}</td>
                <StatCells s={r.s1} />
                <StatCells s={r.s2} />
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-600">
        Simulation follows the exact live engine rules for each strategy · exits by 14:30 IST · past performance does not guarantee future results
      </div>
    </div>
  );
}
